"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";

import { API_BASE_URL } from "@/lib/api-base";
import { isUsableWebSession, useAuthStore } from "@/lib/auth-store";
import { startContextualChat } from "@/lib/client-api";

/**
 * CTA button for sponsored feed cards (FEAT-AGRIBUSINESS-001). Fires a
 * best-effort click beacon, then opens a contextual business conversation
 * for signed-in users. Signed-out visitors continue through authentication.
 */
export function SponsoredCta({
  campaignId,
  label,
  clickToken,
  targetUserId,
  contextTitle,
}: {
  campaignId: string;
  label: string;
  clickToken?: string;
  targetUserId?: string;
  contextTitle: string;
}) {
  const router = useRouter();
  const user = useAuthStore((state) => state.user);
  const accessToken = useAuthStore((state) => state.accessToken);
  const demoMode = useAuthStore((state) => state.demoMode);
  const hasSession = isUsableWebSession({ user, accessToken, demoMode });
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (busy) return;
    if (campaignId) {
      void fetch(`${API_BASE_URL}/campaign-delivery/campaigns/${campaignId}/click`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          surface: accessToken ? "web_authenticated_feed" : "web_public_feed",
          clickToken,
        }),
        keepalive: true,
      }).catch(() => undefined);
    }

    if (!hasSession || demoMode || !targetUserId) {
      router.push(hasSession ? "/messages" : "/auth?next=/messages");
      return;
    }

    setBusy(true);
    try {
      const { href } = await startContextualChat({
        targetUserId,
        contextType: "campaign",
        contextTitle,
        contextId: campaignId,
      });
      router.push(href);
    } catch {
      router.push("/messages");
    }
  };

  return (
    <button type="button" className="link-pill" onClick={handleClick} disabled={busy}>
      {busy ? "Opening chat..." : label}
    </button>
  );
}
